<template>
  <!-- The Inbox as the app draws it after a call (Workspace369 origin/main, Oct 22 2026), at the app's
       own scale. Wide containers get the desktop shell: SidebarV2 with Communications › Inbox, the canvas
       header, and inbox_screen.dart's panes: ConversationListPanel at 28% (conversation_list_panel.dart:
       "Inbox" header and its buttons, InboxSearchBar, InboxFilterBar's two segmented controls with Open and
       All assignees selected; the saved-views and channel bars are hidden by default) and
       ConversationThreadPanel (_ThreadHeader: name, number, "+ Tag", assignee, the action buttons; the date
       chip; MessageBubble; CallMessageCard with InlineAudioPlayer and AiCallSummaryCard; the composer).
       Below 600px it is the phone thread the app pushes under 850pt (inbox_thread_screen.dart: app bar with
       the name and number, Contact and More). The call card's strings are hard-coded in
       call_message_card.dart and ai_call_summary_card.dart; the rest are app_en.arb strings. Northstar
       Studio, Maya Ortiz, the messages and the summary are sample data. The call walks ended (the
       call_ended_banner.dart toast over the new card) → recording (the player) → summary (the AI call
       summary with next steps); the server renders `summary`. The thread is bottom-anchored like the app's
       reversed list, so a growing card pushes older messages up inside a fixed frame. Radius exceptions,
       all the app's own: message bubbles 16 (4 on the sender's corner), the assignee chip 20, the date chip 12, the toast 14. The filter bar
       scrolls sideways in the app, so its clipped end fades; the thread's top edge fades as a scrolled list.
       The Communications hero (comms mode) is one conversation over three channels, as conversation_thread_panel.dart
       draws it: a ChannelDivider at each channel switch, yesterday's EmailMessageCard, today's CallMessageCard
       missed call (Call Back, Remind me), the missed-call text-back (an ordinary SMS bubble, the setting's
       default text) and the client's SMS; the composer's ChannelSwitcher pill moves from Email to SMS. It walks
       email → missed → sms; the server renders `sms`, where the call card and both texts are in view under the
       email. Later messages are laid out from the start and the list sits lower by their height until they
       arrive. Radius exceptions, the app's own: the switcher pill 10, the email card 12. -->
  <div ref="root" class="pcx" :class="`pcx--${state}`" aria-hidden="true">
    <svg class="pcx-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`pcx-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="pcx-desk">
      <aside class="pcx-side">
        <div class="pcx-side-top"><svg class="ic"><use href="#pcx-view_sidebar" /></svg></div>
        <div class="pcx-ws"><span class="pcx-logo">N</span><span class="pcx-ell">Northstar Studio</span><svg class="ic sm"><use href="#pcx-unfold_more" /></svg></div>
        <nav class="pcx-nav">
          <template v-for="g in nav" :key="g.k">
            <div class="pcx-ni top">
              <svg class="ic"><use :href="`#pcx-${g.i}`" /></svg><span>{{ g.k }}</span>
              <svg v-if="g.kids || g.chev" class="ic sm pcx-chev"><use :href="`#pcx-${g.open ? 'expand_more' : 'chevron_right'}`" /></svg>
            </div>
            <template v-if="g.open">
              <div v-for="c in g.kids" :key="c.k" class="pcx-ni child" :class="{ sel: c.k === 'Inbox', off: c.soon }">
                <svg class="ic"><use :href="`#pcx-${c.i}`" /></svg><span>{{ c.k }}</span>
                <b v-if="c.n" class="pcx-count">{{ c.n }}</b>
                <em v-if="c.soon" class="pcx-soon">Coming Soon</em>
              </div>
            </template>
          </template>
        </nav>
        <div class="pcx-side-foot">
          <div v-for="f in foot" :key="f.k" class="pcx-ni"><svg class="ic"><use :href="`#pcx-${f.i}`" /></svg><span>{{ f.k }}</span></div>
        </div>
      </aside>

      <div class="pcx-main">
        <header class="pcx-head">
          <span class="pcx-title"><svg class="ic sm"><use href="#pcx-inbox" /></svg>Inbox</span>
          <span class="pcx-hsearch"><svg class="ic sm"><use href="#pcx-search" /></svg>Search...<kbd>⌘K</kbd></span>
          <span class="pcx-icbtn pcx-bell"><svg class="ic"><use href="#pcx-notifications" /></svg><b>3</b></span>
          <span class="pcx-icbtn"><svg class="ic"><use href="#pcx-settings" /></svg></span>
          <span class="pcx-icbtn"><svg class="ic"><use href="#pcx-chat_bubble_outline" /></svg></span>
        </header>

        <section class="pcx-sheet">
          <!-- Conversation list -->
          <div class="pcx-list">
            <div class="pcx-list-head">
              <b>Inbox</b>
              <span class="pcx-newmsg"><svg class="ic"><use href="#pcx-addComment" /></svg></span>
              <svg class="ic q"><use href="#pcx-groupAdd" /></svg>
              <svg class="ic q"><use href="#pcx-cellTower" /></svg>
              <svg class="ic q"><use href="#pcx-settings" /></svg>
            </div>
            <div class="pcx-lsearch"><svg class="ic"><use href="#pcx-search" /></svg>Search conversations...</div>
            <div class="pcx-filters">
              <span class="pcx-seg"><span class="on">Open</span><span>Unread<i>2</i></span><span>Voicemail</span><span>Snoozed</span></span>
            </div>
            <div class="pcx-rows">
              <div v-for="r in convos" :key="r.n" class="pcx-row" :class="{ sel: r.sel, unread: r.unread }">
                <i class="pcx-dot" />
                <span class="pcx-av">{{ r.a }}</span>
                <span class="pcx-row-t">
                  <span class="pcx-row-top"><b>{{ r.n }}</b><time>{{ r.sel ? (comms ? commsRow.t : voice ? "3:33 PM" : callTime) : r.t }}</time></span>
                  <span class="pcx-prev">{{ r.sel && comms ? commsRow.p : r.sel && voice ? reply : r.p }}</span>
                  <span class="pcx-badges">
                    <template v-if="!(r.sel && comms)"><em v-for="tg in r.tags" :key="tg.k" class="pcx-tag" :style="{ '--dot': tg.c }">{{ tg.k }}</em></template>
                    <em v-for="c in chsOf(r)" :key="c" class="pcx-ch" :class="c.toLowerCase()">{{ c }}</em>
                  </span>
                </span>
              </div>
            </div>
          </div>

          <!-- Thread -->
          <div class="pcx-thread">
            <div class="pcx-th-head">
              <div class="pcx-th-row">
                <span class="pcx-th-name"><b>Maya Ortiz</b><small>+1 (512) 555-0182</small></span>
                <span class="pcx-addtag">+ Tag</span>
              </div>
              <span class="pcx-assignee"><svg class="ic"><use href="#pcx-personOutline" /></svg>Jordan Lee<svg class="ic"><use href="#pcx-expand_more" /></svg></span>
              <div class="pcx-acts">
                <span v-for="b in actions" :key="b.k" class="pcx-act"><svg class="ic"><use :href="`#pcx-${b.i}`" /></svg>{{ b.k }}</span>
              </div>
            </div>
            <div class="pcx-th-body">
              <div v-if="comms" class="pcx-msgs"><CommsThread /></div>
              <div v-else class="pcx-msgs">
                <span class="pcx-day">Today</span>
                <div class="pcx-msg in">
                  <span class="pcx-mav">M</span>
                  <span class="pcx-mcol"><small>Maya Ortiz</small><span class="pcx-bub">Can we go over the menu boards today?</span><time>3:31 PM</time></span>
                </div>
                <div class="pcx-msg out">
                  <span class="pcx-mcol"><span class="pcx-bub">{{ voice ? reply : "Sure, calling you now." }}</span><time>3:33 PM<svg class="ic"><use href="#pcx-doneAll" /></svg></time></span>
                </div>
                <CallCard v-if="!voice" />
              </div>
            </div>
            <Composer />
            <Toast v-if="!voice && !comms" />
          </div>
        </section>
      </div>
      <MotionCallOverlayApp v-if="voice" :state="state" />
    </div>

    <!-- Phone -->
    <div class="pcx-phone">
      <header class="pcp-bar">
        <svg class="ic"><use href="#pcx-arrowBackIosNew" /></svg>
        <span class="pcp-name"><b>Maya Ortiz</b><small>+1 (512) 555-0182</small></span>
        <svg class="ic"><use href="#pcx-personOutline" /></svg>
        <svg class="ic"><use href="#pcx-moreVert" /></svg>
      </header>
      <div class="pcp-body">
        <div v-if="comms" class="pcx-msgs"><CommsThread /></div>
        <div v-else class="pcx-msgs">
          <span class="pcx-day">Today</span>
          <div class="pcx-msg in">
            <span class="pcx-mav">M</span>
            <span class="pcx-mcol"><small>Maya Ortiz</small><span class="pcx-bub">Can we go over the menu boards today?</span><time>3:31 PM</time></span>
          </div>
          <div class="pcx-msg out">
            <span class="pcx-mcol"><span class="pcx-bub">{{ voice ? reply : "Sure, calling you now." }}</span><time>3:33 PM<svg class="ic"><use href="#pcx-doneAll" /></svg></time></span>
          </div>
          <CallCard v-if="!voice" />
        </div>
      </div>
      <Composer />
      <Toast v-if="!voice && !comms" />
      <MotionCallOverlayApp v-if="voice" :state="state" phone />
    </div>
  </div>
</template>

<script setup>
import { computed, h, nextTick, onMounted, ref, watch } from "vue";
import { appIcons } from "~/data/app-icons.js";
import { postCallIcons } from "~/data/app-icons-postcall.js";
import { commsIcons } from "~/data/app-icons-comms.js";

const root = ref(null);
const props = defineProps({
  // ended | recording | summary, or a live call (the Workspace Voice hero): ringing | oncall | transfer,
  // or one conversation over three channels (the Communications hero): missed | sms | email
  state: { type: String, default: "summary" },
});
// A live call: the call overlay floats over the conversation (CallOverlayApp); nothing is in the thread yet.
const voice = computed(() => ["ringing", "oncall", "transfer"].includes(props.state));
const reply = "Yes, call me whenever you're free.";
const chOf = (r) => (r.sel && voice.value ? "SMS" : r.ch);
const comms = computed(() => ["email", "missed", "sms"].includes(props.state));
const commsStep = computed(() => ["email", "missed", "sms"].indexOf(props.state));
// The list row follows the latest message; a conversation over several channels carries a badge for each.
const commsRow = computed(() => [
  { t: "Yesterday", p: "Spring menu board brief" },
  { t: "10:12 AM", p: "Phone call" },
  { t: "10:20 AM", p: "Thanks, call me after 2!" },
][Math.max(0, commsStep.value)]);
const chsOf = (r) => (r.sel && comms.value ? [["Email"], ["Call", "Email"], ["SMS", "Call", "Email"]][commsStep.value] : [chOf(r)]);

// Before the summary lands the thread sits lower by what is still hidden, as the app's reversed list would. A
// transform measured from the laid-out card, so the move never counts as a layout shift; the card is clipped
// above its hidden parts, its notes row and bottom edge lifted to meet them. The server renders
// `summary`, where nothing is hidden.
const hiddenHeight = (el, s) => {
  const h = (x) => (x ? x.offsetHeight + parseFloat(getComputedStyle(x).marginTop) : 0);
  if (comms.value) {
    let sum = 0;
    for (const x of el.querySelectorAll("[data-at]")) if (+x.dataset.at > commsStep.value) sum += h(x);
    return sum;
  }
  const player = h(el.querySelector(".pcx-player"));
  const sum = h(el.querySelector(".pcx-sum"));
  return s === "ended" ? player + sum : s === "recording" ? sum : 0;
};
const place = () => {
  for (const el of root.value?.querySelectorAll(".pcx-msgs") || []) {
    let hide = hiddenHeight(el, props.state);
    // Comms: once the missed call is in, the thread never scrolls its card under the top fade; on a short thread
    // (tablet) the newest message then sits below the composer instead, as a list scrolled up by one.
    const call = comms.value && commsStep.value >= 1 ? el.querySelector(".pcx-call") : null;
    if (call) hide = Math.max(hide, 40 - (el.offsetTop + call.offsetTop));
    el.style.transform = `translateY(${hide}px)`;
    if (!comms.value) el.querySelector(".pcx-call-in")?.style.setProperty("--hide", `${hide}px`);
    // Comms: a message either edge would slice by more than a third is hidden whole; a smaller cut at the
    // top stays under the thread's top fade, as a scrolled list reads.
    else {
      const viewH = el.parentElement.clientHeight;
      for (const x of el.children) {
        const top = el.offsetTop + x.offsetTop + hide;
        x.classList.toggle("pcx-cut", top < -x.offsetHeight / 3 || top + x.offsetHeight - viewH > x.offsetHeight / 3);
      }
    }
  }
  // The sidebar and the conversation list scroll in the app; a row their bottom edge would slice is left out whole.
  for (const nav of root.value?.querySelectorAll(".pcx-nav") || [])
    for (const x of nav.children) x.classList.toggle("pcx-cut", x.offsetTop + x.offsetHeight > nav.clientHeight);
  for (const rows of root.value?.querySelectorAll(".pcx-rows") || []) {
    const end = rows.closest(".pcx-list")?.getBoundingClientRect().bottom;
    for (const x of rows.children) x.classList.toggle("pcx-cut", end !== undefined && x.getBoundingClientRect().bottom > end + 1);
  }
};
onMounted(() => {
  place();
  document.fonts?.ready.then(place);
});
watch(() => props.state, () => nextTick(place));

const shared = ["account_balance_wallet", "auto_awesome", "chat_bubble_outline", "chevron_right", "dashboard", "expand_more", "forum", "handyman", "logout", "notifications", "people_alt", "search", "settings", "support_agent", "unfold_more", "view_sidebar", "work_outline"];
const icons = { ...Object.fromEntries(shared.map((k) => [k, appIcons[k]])), ...postCallIcons, ...commsIcons };

const nav = [
  { k: "Dashboard", i: "dashboard" },
  { k: "AI AutoPilot", i: "auto_awesome", chev: true },
  { k: "Clients", i: "people_alt", kids: [] },
  { k: "Work", i: "work_outline", kids: [] },
  { k: "Finance", i: "account_balance_wallet", kids: [] },
  {
    k: "Communications", i: "forum", open: true,
    kids: [
      { k: "Inbox", i: "inbox", n: 2 },
      { k: "Emails", i: "mailOutline" },
      { k: "Mobile", i: "phoneIphone" },
      { k: "Campaigns", i: "campaign", soon: true },
      { k: "Workflows", i: "playArrow" },
      { k: "Funnels", i: "web" },
    ],
  },
  { k: "Tools", i: "handyman", kids: [] },
];
const foot = [
  { k: "Settings", i: "settings" },
  { k: "Support", i: "support_agent" },
  { k: "Log Out", i: "logout" },
];
const actions = [
  { k: "Search", i: "search" },
  { k: "Pinned messages", i: "pushPin" },
  { k: "Contact", i: "personOutline" },
  { k: "Call", i: "phone" },
  { k: "Status", i: "checkCircleOutline" },
  { k: "More", i: "moreVert" },
];
const callTime = "3:42 PM";
const convos = [
  { n: "Maya Ortiz", a: "MO", p: "Phone call", ch: "Call", sel: true, tags: [{ k: "Follow-up", c: "#f59e0b" }] },
  { n: "Juniper Bakery", a: "JB", t: "2:58 PM", p: "Can we move Thursday's visit to Friday?", ch: "SMS", unread: true, tags: [] },
  { n: "Harbor & Co.", a: "HC", t: "1:12 PM", p: "Thanks, the files look great.", ch: "Email", tags: [] },
  { n: "Brightside Dental", a: "BD", t: "11:40 AM", p: "Voicemail", ch: "VM", unread: true, tags: [{ k: "New Lead", c: "#06b6d4" }] },
  { n: "Atlas Logistics", a: "AL", t: "Yesterday", p: "Got it, paying the balance today.", ch: "SMS", tags: [] },
  { n: "Orchard Veterinary", a: "OV", t: "Yesterday", p: "Can you send the signed copy?", ch: "Email", tags: [] },
];

// CallMessageCard: header, the recording player, the AI summary, the notes row. Both later parts are always laid
// out (hidden until their state) so no state changes the thread's layout.
const CallCard = () =>
  h("div", { class: "pcx-call" }, [
    h("div", { class: "pcx-call-in" }, [
      h("div", { class: "pcx-call-head" }, [
        h("svg", { class: "ic pcx-green" }, [h("use", { href: "#pcx-phoneCallback" })]),
        h("b", "Call ended"),
        h("span", { class: "pcx-call-meta" }, "Outbound · 06:41"),
        h("i", { class: "pcx-grow" }),
        h("time", callTime),
      ]),
      h("div", { class: "pcx-player" }, [
        h("span", { class: "pcx-play" }, [h("svg", { class: "ic" }, [h("use", { href: "#pcx-playArrow" })])]),
        h("span", { class: "pcx-track" }, [h("i")]),
        h("span", { class: "pcx-ptime" }, "00:00 / 06:41"),
      ]),
      h("div", { class: "pcx-sum" }, [
        h("div", { class: "pcx-sum-head" }, [
          h("svg", { class: "ic" }, [h("use", { href: "#pcx-auto_awesome" })]),
          h("b", "Call summary"),
          h("span", "Powered by AI"),
          h("i", { class: "pcx-grow" }),
          h("svg", { class: "ic pcx-quiet" }, [h("use", { href: "#pcx-refresh" })]),
        ]),
        h("p", "•  Maya approved the second menu board layout."),
        h("p", "•  The latte price changes to $5.25 on both boards."),
        h("p", "•  She wants two window decals in the same style."),
        h("b", { class: "pcx-next" }, "Next steps"),
        h("p", "•  Send a proposal for the two window decals."),
        h("p", "•  Book the install visit for March 12."),
      ]),
      h("div", { class: "pcx-note" }, [
        h("svg", { class: "ic" }, [h("use", { href: "#pcx-note" })]),
        h("span", "Add note..."),
        h("svg", { class: "ic" }, [h("use", { href: "#pcx-edit" })]),
      ]),
    ]),
  ]);


// call_ended_banner.dart: shown when the call ends, gone by the time the summary lands.
const Toast = () =>
  h("div", { class: "pcx-toast" }, [
    h("svg", { class: "ic" }, [h("use", { href: "#pcx-callEnd" })]),
    h("span", [
      "Call with Maya Ortiz ended · ",
      h("b", "06:41"),
      " · ",
      h("b", { class: "pcx-link" }, "View summary →"),
    ]),
    h("svg", { class: "ic pcx-x" }, [h("use", { href: "#pcx-close" })]),
  ]);

const icon = (name, cls = "ic") => h("svg", { class: cls }, [h("use", { href: `#pcx-${name}` })]);

// ChannelDivider (channel_divider.dart): lines either side of the new channel's icon and label.
const Divider = (at, label, i, c) =>
  h("div", { class: ["pcx-chdiv", { "pcx-later": at }], "data-at": at || undefined, style: { "--chc": c } }, [h("i"), icon(i), h("span", label), h("i")]);

// Comms: missed call (CallMessageCard) → the text-back → Maya's SMS → her email (EmailMessageCard). data-at is
// the step a part arrives on; until then it is laid out but hidden, and the list sits lower by its height.
const CommsThread = () => [
  h("span", { class: "pcx-day" }, "Yesterday"),
  Divider(0, "Email", "emailIcon", "#059669"),
  h("div", { class: "pcx-email" }, [
    h("div", { class: "pcx-email-in" }, [
      h("div", { class: "pcx-email-top" }, [icon("mailOutline"), h("b", "Maya Ortiz"), h("time", "Oct 21 · 4:12 PM")]),
      h("b", { class: "pcx-email-subj" }, "Spring menu board brief"),
      h("p", "Hi Jordan, here is the brief for the spring boards. Could you send a proposal this week?"),
      h("div", { class: "pcx-email-foot" }, [h("span", ["Open email", icon("arrowForward")]), h("i", { class: "pcx-grow" }), icon("attachFile"), h("span", "2")]),
    ]),
  ]),
  h("span", { class: "pcx-day pcx-later", "data-at": 1 }, "Today"),
  Divider(1, "Phone Call", "phone", "#7C3AED"),
  h("div", { class: "pcx-call pcx-later", "data-at": 1 }, [
    h("div", { class: "pcx-call-in" }, [
      h("div", { class: "pcx-call-head" }, [icon("phoneMissed", "ic pcx-red"), h("b", "Missed call"), h("i", { class: "pcx-grow" }), h("time", "10:12 AM")]),
      h("div", { class: "pcx-note pcx-note-flat" }, [icon("note"), h("span", "Add note..."), icon("edit")]),
      h("div", { class: "pcx-callacts" }, [h("span", [icon("callIcon"), "Call Back"]), h("span", [icon("alarmIcon"), "Remind me"])]),
    ]),
  ]),
  Divider(2, "SMS", "smsIcon", "#2563EB"),
  h("div", { class: "pcx-msg out pcx-later", "data-at": 2 }, [
    h("span", { class: "pcx-mcol" }, [
      h("span", { class: "pcx-bub" }, "Sorry we missed your call! We'll get back to you shortly."),
      h("time", ["10:13 AM", icon("doneAll")]),
    ]),
  ]),
  h("div", { class: "pcx-msg in pcx-later", "data-at": 2 }, [
    h("span", { class: "pcx-mav" }, "M"),
    h("span", { class: "pcx-mcol" }, [h("small", "Maya Ortiz"), h("span", { class: "pcx-bub" }, "Thanks, call me after 2!"), h("time", "10:20 AM")]),
  ]),
];

// MessageComposer: in comms mode the collapsed composer with the ChannelSwitcher pill, which follows the channel in use.
const Composer = () => {
  if (!comms.value) return h("div", { class: "pcx-composer" }, [h("span", "Write a message... (type / for quick replies)")]);
  const email = commsStep.value === 0;
  return h("div", { class: "pcx-composer pcx-composer--ch" }, [
    h("div", { class: "pcx-cbox" }, [
      h("span", { class: ["pcx-chpill", email ? "email" : "sms"] }, [icon(email ? "mailOutline" : "smsIcon"), email ? "Email" : "SMS", icon("arrowDropDown")]),
      h("span", { class: "pcx-chint" }, email ? "Reply by email..." : "Reply by SMS..."),
      icon("edit", "ic pcx-cedit"),
    ]),
  ]);
};
</script>

<style>
/* Tokens: shadcn Neutral (FlexScheme index 58, the app's default) blended by AppShellPalette, as in
   InvoicesAppWindow; the thread's surfaceContainerLowest page and the list's segmented control. */
.pcx {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa;
  --fill: #eef1f4; --canvas: #f7f7f7; --page: #fff; --edge: #ebebeb; --card-edge: #e6e6e6; --line2: #efefef;
  --ink2: #4f4f4f; --ink3: #717171; --quiet: #a7a7a7; --selected: rgb(10 10 10 / 0.06);
  --surface: #fff; --thread: #fafafa; --divider: rgb(0 0 0 / 0.12); --sum: rgb(10 10 10 / 0.025); --sum-line: rgb(10 10 10 / 0.08);
  --toast: #262626; --on-toast: #fafafa; --toast-link: #fafafa;
  position: relative;
  container-type: inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .pcx {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717;
  --fill: #232527; --canvas: #121212; --page: #1a1a1a; --edge: #272727; --card-edge: #2c2c2c; --line2: #252525;
  --ink2: #c2c2c2; --ink3: #9a9a9a; --quiet: #6b6b6b; --selected: rgb(250 250 250 / 0.1);
  --surface: #1f1f1f; --thread: #141414; --divider: rgb(255 255 255 / 0.12); --sum: rgb(250 250 250 / 0.025); --sum-line: rgb(250 250 250 / 0.08);
  --toast: #f5f5f5; --on-toast: #171717; --toast-link: #171717;
}
/* Size reserved before the markup streams in (the G1 pattern): 600 / 540 / the phone's 390:700. */
.pcx { min-height: 600px; }
@media (max-width: 1128px) { .pcx { min-height: 540px; } }
@media (max-width: 648px) { .pcx { max-width: 390px; min-height: 0; margin-inline: auto; aspect-ratio: 390 / 700; } }
.pcx-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.pcx .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.pcx .ic.sm { width: 16px; height: 16px; }
.pcx-grow { flex: 1; }
.pcx-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ---------- desktop ---------- */
.pcx-desk {
  position: relative; display: grid; grid-template-columns: 252px minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); height: 600px; overflow: hidden;
  border: 1px solid var(--card-edge); border-radius: 10px; background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
}
.dark .pcx-desk { border-color: #3a3a3a; box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.pcx-side { display: flex; flex-direction: column; min-height: 0; padding: 0 10px 10px; }
.pcx-side-top { display: flex; align-items: center; height: 48px; padding-left: 6px; color: var(--ink2); }
.pcx-ws { display: flex; align-items: center; gap: 10px; height: 40px; margin-bottom: 6px; border-bottom: 1px solid var(--edge); padding: 0 6px 6px; font-weight: 600; }
.pcx-ws .ic { margin-left: auto; color: var(--ink3); }
.pcx-logo { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: #171717; color: #fafafa; font-size: 12px; font-weight: 700; }
.dark .pcx-logo { background: #fafafa; color: #171717; }
.pcx-nav { position: relative; flex: 1; min-height: 0; overflow: hidden; }
.pcx-nav > .pcx-cut, .pcx-rows > .pcx-cut { visibility: hidden; }
.pcx-ni { display: flex; align-items: center; gap: 10px; height: 32px; padding: 0 8px; border-radius: 6px; color: var(--ink2); white-space: nowrap; }
.pcx-ni.top { color: var(--ink); font-weight: 600; }
.pcx-ni.child { margin-left: 12px; }
.pcx-ni.child .ic { width: 18px; height: 18px; color: var(--ink3); }
.pcx-ni.sel { background: var(--selected); color: var(--ink); font-weight: 500; }
.pcx-ni.off { opacity: 0.55; }
.pcx-ni .pcx-chev { margin-left: auto; color: var(--ink3); }
.pcx-count { margin-left: auto; min-width: 18px; border-radius: 4px; background: var(--selected); padding: 1px 5px; font-size: 11px; text-align: center; }
.pcx-soon { margin-left: auto; border-radius: 4px; background: var(--selected); padding: 1px 5px; color: var(--ink3); font-size: 10px; font-style: normal; }
.pcx-side-foot { border-top: 1px solid var(--edge); padding-top: 6px; }
.pcx-side-foot .pcx-ni { height: 30px; }
.pcx-main { display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; }
.pcx-head { display: flex; align-items: center; gap: 12px; padding: 0 14px 0 4px; }
.pcx-title { display: flex; flex: 1; align-items: center; gap: 8px; font-weight: 600; }
.pcx-title .ic { color: var(--ink2); }
.pcx-hsearch { display: flex; align-items: center; gap: 8px; width: 230px; height: 30px; border: 1px solid var(--card-edge); border-radius: 6px; background: var(--page); padding: 0 6px 0 10px; color: var(--ink3); }
.pcx-hsearch kbd { margin-left: auto; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 5px; font: inherit; font-size: 11px; }
.pcx-icbtn { position: relative; display: grid; place-items: center; width: 28px; height: 28px; color: var(--ink2); }
.pcx-bell b { position: absolute; top: 0; right: 0; display: grid; place-items: center; min-width: 14px; height: 14px; border-radius: 4px; background: #e53935; color: #fff; font-size: 9px; }
.pcx-sheet { display: grid; grid-template-columns: 28% minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); min-height: 0; overflow: hidden; border-top: 1px solid var(--edge); border-left: 1px solid var(--edge); border-top-left-radius: 10px; background: var(--page); }

/* Conversation list */
.pcx-list { display: flex; flex-direction: column; min-width: 0; min-height: 0; overflow: hidden; border-right: 1px solid var(--edge); }
.pcx-list-head { display: flex; align-items: center; gap: 4px; padding: 16px 12px 4px 16px; }
.pcx-list-head b { flex: 1; font-size: 18px; }
.pcx-list-head .q { width: 20px; height: 20px; margin: 0 4px; color: var(--ink3); }
.pcx-newmsg { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 8px; background: var(--surface); }
.pcx-newmsg .ic { width: 17px; height: 17px; }
.pcx-lsearch { display: flex; align-items: center; gap: 6px; height: 32px; margin: 8px 12px; border: 1px solid rgb(115 115 115 / 0.24); border-radius: 8px; background: var(--surface); padding: 0 12px; color: var(--quiet); }
.pcx-lsearch .ic { width: 16px; height: 16px; color: var(--ink3); }
.pcx-filters { overflow: hidden; padding: 4px 12px; -webkit-mask-image: linear-gradient(90deg, #000 78%, transparent); mask-image: linear-gradient(90deg, #000 78%, transparent); }
.pcx-seg { display: inline-flex; height: 30px; border-radius: 10px; background: var(--selected); padding: 3px; white-space: nowrap; }
.pcx-seg span { display: inline-flex; align-items: center; gap: 6px; border-radius: 8px; padding: 0 12px; color: var(--ink3); font-size: 12px; font-weight: 500; }
.pcx-seg i { font-size: 11px; font-style: normal; opacity: 0.6; }
.pcx-seg .on { border: 1px solid rgb(10 10 10 / 0.08); background: #fff; box-shadow: 0 1px 4px rgb(0 0 0 / 0.1); color: var(--ink); font-weight: 600; }
.dark .pcx-seg .on { border-color: rgb(250 250 250 / 0.08); background: #333; }
.pcx-rows { margin-top: 8px; }
.pcx-row { position: relative; display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px 10px 9px; border-left: 3px solid transparent; }
.pcx-row.sel { border-left-color: var(--primary); background: rgb(23 23 23 / 0.08); }
.dark .pcx-row.sel { background: rgb(250 250 250 / 0.08); }
.pcx-dot { flex: none; width: 8px; height: 8px; margin: 16px 0 0 3px; border-radius: 50%; }
.pcx-row.unread .pcx-dot { background: var(--primary); }
.pcx-av { display: grid; flex: none; place-items: center; width: 40px; height: 40px; border-radius: 50%; background: rgb(23 23 23 / 0.1); font-size: 13px; font-weight: 600; }
.dark .pcx-av { background: rgb(250 250 250 / 0.1); }
.pcx-row-t { display: grid; flex: 1; gap: 2px; min-width: 0; }
.pcx-row-top { display: flex; align-items: baseline; gap: 6px; }
.pcx-row-top b { flex: 1; overflow: hidden; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.pcx-row.unread .pcx-row-top b { font-weight: 700; }
.pcx-row-top time { color: var(--ink3); font-size: 11px; }
.pcx-prev { overflow: hidden; color: var(--ink2); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.pcx-badges { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.pcx-tag, .pcx-ch { display: inline-flex; align-items: center; gap: 4px; height: 18px; border-radius: 4px; background: var(--selected); padding: 0 6px; font-size: 12px; font-style: normal; }
.pcx-tag::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: var(--dot); }
.pcx-ch.sms { background: #ebf0fd; color: #3f58bf; } .pcx-ch.email { background: #e6f4ea; color: #2d7a4e; }
.pcx-ch.call { background: #f4ecfc; color: #7948af; } .pcx-ch.vm { background: #fdf4c3; color: #946000; }
.dark .pcx-ch.sms { background: rgb(91 127 234 / 0.2); color: #93aef9; } .dark .pcx-ch.email { background: rgb(63 178 110 / 0.18); color: #6ed39a; }
.dark .pcx-ch.call { background: rgb(154 111 224 / 0.2); color: #bea5f2; } .dark .pcx-ch.vm { background: rgb(224 170 36 / 0.17); color: #f1c95e; }

/* Thread */
.pcx-thread { position: relative; display: flex; flex-direction: column; min-width: 0; min-height: 0; background: var(--thread); }
.pcx-th-head { flex: none; border-bottom: 1px solid var(--edge); background: var(--page); padding: 12px 16px 8px; }
.pcx-th-row { display: flex; align-items: center; gap: 8px; }
.pcx-th-name { display: grid; flex: 1; gap: 1px; }
.pcx-th-name b { font-size: 15px; font-weight: 600; }
.pcx-th-name small { color: var(--ink3); font-size: 12px; }
.pcx-addtag { border: 1px dashed var(--divider); border-radius: 4px; padding: 2px 6px; color: var(--ink3); font-size: 11px; }
/* Radius 20 is the app's own (conversation_thread_panel.dart _AssigneeRow, SmoothBorderRadius 20). */
.pcx-assignee { display: inline-flex; align-items: center; gap: 4px; margin-top: 6px; border: 1px solid rgb(23 23 23 / 0.25); border-radius: 20px; background: rgb(23 23 23 / 0.08); padding: 4px 10px; font-size: 12px; font-weight: 500; }
.dark .pcx-assignee { border-color: rgb(250 250 250 / 0.25); background: rgb(250 250 250 / 0.08); }
.pcx-assignee .ic { width: 13px; height: 13px; }
.pcx-acts { display: flex; gap: 6px; margin-top: 8px; overflow: hidden; }
.pcx-act { display: inline-flex; flex: none; align-items: center; gap: 5px; border: 1px solid var(--divider); border-radius: 6px; padding: 5px 8px; font-size: 12px; font-weight: 500; white-space: nowrap; }
.pcx-act .ic { width: 16px; height: 16px; color: var(--ink3); }
.pcx-th-body { position: relative; flex: 1; min-height: 0; overflow: hidden; -webkit-mask-image: linear-gradient(transparent, #000 40px); mask-image: linear-gradient(transparent, #000 40px); }
/* Bottom-anchored like the app's reversed list. */
.pcx-msgs { position: absolute; right: 0; bottom: 0; left: 0; display: flex; flex-direction: column; padding-bottom: 6px; }
/* DateSeparator radius 12 (conversation_thread_panel.dart _DateSeparator), the app's own. */
.pcx-day { align-self: center; margin: 16px 0; border-radius: 12px; background: var(--surface); padding: 4px 12px; color: var(--ink3); font-size: 11px; font-weight: 500; }
.pcx-msg { display: flex; align-items: flex-end; gap: 8px; padding: 4px 16px; }
.pcx-msg.out { justify-content: flex-end; }
.pcx-mav { display: grid; flex: none; place-items: center; width: 28px; height: 28px; border-radius: 50%; background: rgb(23 23 23 / 0.1); font-size: 11px; font-weight: 600; }
.dark .pcx-mav { background: rgb(250 250 250 / 0.1); }
.pcx-mcol { display: flex; flex-direction: column; align-items: flex-start; max-width: 65%; }
.pcx-msg.out .pcx-mcol { align-items: flex-end; }
.pcx-mcol small { padding: 0 0 2px 4px; color: var(--ink3); font-size: 11px; font-weight: 600; }
.pcx-bub { border-radius: 16px 16px 16px 4px; background: var(--surface); padding: 10px 14px; line-height: 1.4; }
.pcx-msg.out .pcx-bub { border-radius: 16px 16px 4px 16px; background: var(--primary); color: var(--on-primary); }
.pcx-mcol time { display: inline-flex; align-items: center; gap: 4px; padding: 4px 4px 0; color: var(--quiet); font-size: 10px; }
.pcx-mcol time .ic { width: 12px; height: 12px; color: var(--ink3); }
.pcx-call { padding: 6px 16px; }
.pcx-call-in { --hide: 0px; position: relative; border: 1px solid var(--divider); border-radius: 12px; background: var(--surface); padding: 14px; clip-path: inset(0 0 var(--hide) 0 round 12px); transition: clip-path 0.55s cubic-bezier(0.3, 0, 0.2, 1); }
.pcx-call-in::after { position: absolute; right: -1px; bottom: -1px; left: -1px; height: 14px; border: 1px solid var(--divider); border-top: 0; border-radius: 0 0 12px 12px; content: ""; transform: translateY(calc(-1 * var(--hide))); transition: transform 0.55s cubic-bezier(0.3, 0, 0.2, 1); }
.pcx--summary .pcx-call-in::after { opacity: 0; transition: transform 0.55s, opacity 0s 0.55s; }
.pcx-call-head { display: flex; align-items: center; gap: 8px; }
.pcx-call-head b { font-weight: 600; }
.pcx-green { color: #059669; }
.pcx-call-meta { color: var(--ink3); font-size: 12px; }
.pcx-call-head time { color: var(--quiet); font-size: 11px; }
.pcx-player { display: flex; align-items: center; gap: 8px; margin-top: 10px; border-radius: 8px; background: var(--thread); padding: 4px 8px; transition: opacity 0.5s ease 0.2s; }
.pcx-play { display: grid; flex: none; place-items: center; width: 32px; height: 32px; color: var(--primary); }
.pcx-play .ic { width: 22px; height: 22px; }
.pcx-track { position: relative; flex: 1; height: 3px; border-radius: 2px; background: var(--divider); }
.pcx-track i { position: absolute; top: -3.5px; left: 0; width: 10px; height: 10px; border-radius: 50%; background: var(--primary); }
.pcx-ptime { color: var(--ink3); font-size: 11px; }
.pcx-sum { margin-top: 8px; border: 1px solid var(--sum-line); border-radius: 10px; background: var(--sum); padding: 14px; transition: opacity 0.6s ease 0.25s; }
.pcx--ended .pcx-player, .pcx--ended .pcx-sum, .pcx--recording .pcx-sum { visibility: hidden; opacity: 0; transition: none; }
.pcx-msgs { transition: transform 0.55s cubic-bezier(0.3, 0, 0.2, 1); }
.pcx-sum-head { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.pcx-sum-head .ic { width: 14px; height: 14px; color: var(--ink3); }
.pcx-sum-head b { font-weight: 600; }
.pcx-sum-head span { margin-left: 2px; color: var(--ink3); font-size: 11px; }
.pcx-quiet { opacity: 0.6; }
.pcx-sum p { margin: 0 0 4px 4px; font-size: 12px; line-height: 1.4; }
.pcx-next { display: block; margin: 6px 0 4px; font-size: 12px; font-weight: 600; }
.pcx-note { display: flex; align-items: center; gap: 6px; margin-top: 10px; transform: translateY(calc(-1 * var(--hide))); transition: transform 0.55s cubic-bezier(0.3, 0, 0.2, 1); border-radius: 8px; background: rgb(115 115 115 / 0.08); padding: 8px; color: var(--quiet); font-size: 12px; }
.pcx-note span { flex: 1; }
.pcx-note .ic { width: 14px; height: 14px; }
.pcx-composer { flex: none; padding: 8px 16px 12px; background: var(--thread); }
.pcx-composer > span { display: block; border: 1px solid var(--divider); border-radius: 12px; background: var(--page); padding: 10px 14px; color: var(--quiet); }

/* The toast: inverseSurface, top-centre, until the summary lands. */
.pcx-toast {
  position: absolute; z-index: 4; top: 8px; left: 50%; display: flex; align-items: center; gap: 10px;
  width: max-content; max-width: min(420px, 94%); border-radius: 14px; background: var(--toast); padding: 10px 8px 10px 14px;
  color: var(--on-toast); box-shadow: 0 4px 16px rgb(0 0 0 / 0.25); translate: -50% 0; transition: opacity 0.4s ease, transform 0.4s ease;
}
.pcx-toast b { font-weight: 600; }
.pcx-link { color: var(--toast-link); text-decoration: underline; text-underline-offset: 2px; }
.pcx-x { width: 16px !important; height: 16px !important; opacity: 0.7; }
.pcx--summary .pcx-toast { opacity: 0; transform: translateY(-8px); pointer-events: none; }

/* Tablet: the sidebar collapsed, the list at 35% as the app's tablet layout. */
@container (max-width: 1080px) {
  .pcx-desk { grid-template-columns: minmax(0, 1fr); height: 540px; }
  .pcx-side, .pcx-hsearch { display: none; }
  .pcx-head { padding-left: 14px; }
  .pcx-sheet { grid-template-columns: 35% minmax(0, 1fr); }
  .pcx-acts .pcx-act:nth-child(n + 5) { display: none; }
  /* The toast clears the thread header here, where the thread is too narrow to pass beside it. */
  .pcx-thread .pcx-toast { top: 132px; }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.pcx-phone { display: none; }
@container (max-width: 600px) {
  .pcx-desk { display: none; }
  .pcx-phone {
    --u: calc(100cqi / 390); --t: calc(var(--u) * 1.25);
    position: relative; display: flex; flex-direction: column; height: calc(var(--u) * 700); overflow: hidden;
    border: 1px solid var(--card-edge); border-radius: 10px; background: var(--thread); font-size: calc(var(--t) * 13);
  }
  .dark .pcx-phone { border-color: #3a3a3a; }
  .pcx-phone .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
  .pcp-bar { display: flex; flex: none; align-items: center; gap: calc(var(--u) * 14); background: var(--page); padding: calc(var(--u) * 12) calc(var(--u) * 16); }
  .pcp-name { display: grid; flex: 1; min-width: 0; }
  .pcp-name b { font-size: calc(var(--t) * 16); font-weight: 600; }
  .pcp-name small { color: var(--ink3); font-size: calc(var(--t) * 12); }
  .pcp-body { position: relative; flex: 1; min-height: 0; overflow: hidden; }
  .pcx-phone .pcx-day { margin: calc(var(--u) * 16) 0; padding: calc(var(--u) * 4) calc(var(--u) * 12); font-size: calc(var(--t) * 11); }
  .pcx-phone .pcx-msg { gap: calc(var(--u) * 8); padding: calc(var(--u) * 4) calc(var(--u) * 16); }
  .pcx-phone .pcx-mav { width: calc(var(--u) * 28); height: calc(var(--u) * 28); font-size: calc(var(--t) * 11); }
  .pcx-phone .pcx-mcol small, .pcx-phone .pcx-call-head time, .pcx-phone .pcx-ptime, .pcx-phone .pcx-sum-head span { font-size: calc(var(--t) * 11); }
  .pcx-phone .pcx-mcol time { font-size: calc(var(--t) * 10); }
  .pcx-phone .pcx-bub { padding: calc(var(--u) * 10) calc(var(--u) * 14); }
  .pcx-phone .pcx-call { padding: calc(var(--u) * 6) calc(var(--u) * 16); }
  .pcx-phone .pcx-call-in { padding: calc(var(--u) * 14); }
  .pcx-phone .pcx-call-head { flex-wrap: wrap; gap: calc(var(--u) * 4) calc(var(--u) * 8); }
  .pcx-phone .pcx-call-meta, .pcx-phone .pcx-sum p, .pcx-phone .pcx-next, .pcx-phone .pcx-note { font-size: calc(var(--t) * 12); }
  .pcx-phone .pcx-play { width: calc(var(--u) * 32); height: calc(var(--u) * 32); }
  .pcx-phone .pcx-sum { margin-top: calc(var(--u) * 8); padding: calc(var(--u) * 12); }
  .pcx-phone .pcx-sum-head .ic, .pcx-phone .pcx-note .ic { width: calc(var(--u) * 14); height: calc(var(--u) * 14); }
  .pcx-phone .pcx-composer { padding: calc(var(--u) * 8) calc(var(--u) * 16) calc(var(--u) * 14); }
  .pcx-phone .pcx-composer > span { overflow: hidden; padding: calc(var(--u) * 10) calc(var(--u) * 14); text-overflow: ellipsis; white-space: nowrap; }
  .pcx-phone .pcx-toast { top: calc(var(--u) * 64); gap: calc(var(--u) * 8); font-size: calc(var(--t) * 12); }
  .pcx-phone .pcx-toast .ic { width: calc(var(--u) * 18); height: calc(var(--u) * 18); }
}
/* Workspace Voice: the call overlay docks over the thread's bottom right, so the conversation keeps clear of it
   on desktop and, where the card would cover nearly all of it (tablet), sits under it unseen; no message is cut. */
:is(.pcx--ringing, .pcx--oncall, .pcx--transfer) .pcx-desk .pcx-msgs { right: 372px; }
@container (max-width: 1080px) {
  :is(.pcx--ringing, .pcx--oncall, .pcx--transfer) .pcx-desk .pcx-msgs { visibility: hidden; }
}
/* ---------- comms mode (sizes in --k / --kt: 1px on desktop, the phone's app point and text point there) ---------- */
.pcx { --k: 1px; --kt: 1px; }
.pcx-later { transition: opacity 0.45s ease 0.2s; }
.pcx-msgs > .pcx-cut { visibility: hidden; opacity: 0; transition: none; }
.pcx--email .pcx-later, .pcx--missed .pcx-later[data-at="2"] { visibility: hidden; opacity: 0; transition: none; }
.pcx-chdiv { display: flex; align-items: center; padding: calc(var(--k) * 12) calc(var(--k) * 16); color: color-mix(in srgb, var(--ink) 50%, transparent); font-size: calc(var(--kt) * 11); font-weight: 500; }
.pcx-chdiv i { flex: 1; height: 1px; background: var(--divider); }
.pcx-chdiv .ic { width: calc(var(--k) * 14); height: calc(var(--k) * 14); margin: 0 calc(var(--k) * 6) 0 calc(var(--k) * 12); color: var(--chc); }
.pcx-chdiv span { margin-right: calc(var(--k) * 12); }
.pcx-red { color: #ba1a1a; }
.dark .pcx-red { color: #ffb4ab; }
.pcx-note-flat { transform: none; }
.pcx-callacts { display: flex; gap: calc(var(--k) * 8); margin-top: calc(var(--k) * 10); }
.pcx-callacts span { display: inline-flex; align-items: center; gap: calc(var(--k) * 6); border: 1px solid var(--divider); border-radius: 6px; padding: calc(var(--k) * 4) calc(var(--k) * 12); font-size: calc(var(--kt) * 12); font-weight: 500; }
.pcx-callacts .ic { width: calc(var(--k) * 16); height: calc(var(--k) * 16); }
.pcx-email { padding: calc(var(--k) * 8) calc(var(--k) * 16); }
.pcx-email-in { border: 1px solid color-mix(in srgb, var(--ink) 10%, transparent); border-radius: 12px; background: var(--surface); padding: calc(var(--k) * 16); }
.pcx-email-top { display: flex; align-items: center; gap: calc(var(--k) * 8); color: var(--ink3); }
.pcx-email-top .ic { width: calc(var(--k) * 16); height: calc(var(--k) * 16); }
.pcx-email-top b { flex: 1; overflow: hidden; color: var(--ink); font-size: calc(var(--kt) * 12); font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.pcx-email-top time { font-size: calc(var(--kt) * 11); }
.pcx-email-subj { display: block; margin-top: calc(var(--k) * 10); font-size: calc(var(--kt) * 14); font-weight: 600; }
.pcx-email-in p { display: -webkit-box; margin: calc(var(--k) * 5) 0 0; overflow: hidden; color: var(--ink3); font-size: calc(var(--kt) * 12); line-height: 1.5; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.pcx-email-foot { display: flex; align-items: center; gap: calc(var(--k) * 4); margin-top: calc(var(--k) * 12); color: var(--ink3); font-size: calc(var(--kt) * 12); }
.pcx-email-foot > span:first-child { display: inline-flex; align-items: center; gap: calc(var(--k) * 4); color: var(--ink); font-weight: 500; }
.pcx-email-foot .ic { width: calc(var(--k) * 14); height: calc(var(--k) * 14); }
.pcx-composer--ch { border-top: 1px solid color-mix(in srgb, var(--divider) 60%, transparent); padding: calc(var(--k) * 8) calc(var(--k) * 12) calc(var(--k) * 10); }
.pcx-cbox { display: flex; align-items: center; gap: calc(var(--k) * 10); min-height: calc(var(--k) * 48); border: 1px solid var(--divider); border-radius: 12px; background: var(--page); padding: 0 calc(var(--k) * 12) 0 calc(var(--k) * 4); }
.pcx-chpill { display: inline-flex; flex: none; align-items: center; gap: calc(var(--k) * 4); height: calc(var(--k) * 40); border-radius: 10px; padding: 0 calc(var(--k) * 6) 0 calc(var(--k) * 10); font-size: calc(var(--kt) * 11); font-weight: 600; transition: background-color 0.3s, color 0.3s; }
.pcx-chpill.sms { background: rgb(30 64 175 / 0.1); color: #1e40af; }
.pcx-chpill.email { background: rgb(5 150 105 / 0.1); color: #059669; }
.dark .pcx-chpill.sms { color: #93aef9; }
.dark .pcx-chpill.email { color: #6ed39a; }
.pcx-chpill .ic { width: calc(var(--k) * 14); height: calc(var(--k) * 14); }
.pcx-chpill .ic:last-child { width: calc(var(--k) * 16); height: calc(var(--k) * 16); }
.pcx-chint { flex: 1; overflow: hidden; color: var(--quiet); font-size: calc(var(--kt) * 13); text-overflow: ellipsis; white-space: nowrap; }
.pcx-cedit { width: calc(var(--k) * 18) !important; height: calc(var(--k) * 18) !important; color: color-mix(in srgb, var(--ink) 46%, transparent); }
@container (max-width: 600px) {
  .pcx-phone { --k: var(--u); --kt: var(--t); }
}
@media (prefers-reduced-motion: reduce) { .pcx * { animation: none !important; transition: none !important; } }
</style>
